/**
 * Requirements summary:
 * - Keep completed-season team state consistent across list, detail, and edit screens.
 * - Prefer gym-facing labels ("Completed") over database-facing status words ("inactive")
 *   when a team belongs to a finished season.
 * - Treat inactive/completed teams as historical records by default.
 */

import type { Team } from '@/utils/types/team';

export type SeasonLike = {
  season_status?: string | null;
  end_date?: string | null;
  archived_at?: string | null;
} | null | undefined;

export function isCompletedSeason(season: SeasonLike): boolean {
  if (!season) return false;

  if (season.season_status === 'past' || season.season_status === 'archived') {
    return true;
  }

  if (season.archived_at) {
    return true;
  }

  if (!season.end_date) {
    return false;
  }

  const endDate = new Date(`${season.end_date}T23:59:59`);
  if (Number.isNaN(endDate.getTime())) {
    return false;
  }

  return endDate < new Date();
}

export function isHistoricalTeam(team: Pick<Team, 'status'> | null | undefined, season?: SeasonLike): boolean {
  return team?.status === 'inactive' || team?.status === 'archived' || isCompletedSeason(season);
}

export function getTeamStatusLabel(team: Pick<Team, 'status'>, season?: SeasonLike): string {
  if (isCompletedSeason(season)) {
    return 'Completed';
  }

  if (team.status === 'inactive') {
    return 'Inactive';
  }

  if (team.status === 'archived') {
    return 'Archived';
  }

  return 'Active';
}
